/**
 * Dataset viewer turn grouping — mirrors Chat Viewer reasoning / thinking display.
 */

import {
  getDialogStepBodyDisplay,
  getPipelineStepDisplayName,
  isDialogReasoningRole,
} from './vuserDialogDisplay';

const ASSISTANT_SENDERS = new Set([
  'assistant',
  'helpdesk',
  'coordinator',
  'ai',
  'bot',
  'agent',
]);

export function getDatasetTurnSender(turn: Record<string, unknown>): string {
  const raw = turn.sender ?? turn.role;
  return typeof raw === 'string' ? raw.trim().toLowerCase() : '';
}

export function isDatasetReasoningTurn(turn: Record<string, unknown>): boolean {
  return isDialogReasoningRole(getDatasetTurnSender(turn));
}

export function isDatasetAssistantTurn(turn: Record<string, unknown>): boolean {
  const sender = getDatasetTurnSender(turn);
  if (!sender || isDatasetReasoningTurn(turn)) return false;
  if (sender === 'user' || sender === 'customer') return false;
  return ASSISTANT_SENDERS.has(sender);
}

export type DatasetTurnRef = {
  turn: Record<string, unknown>;
  index: number;
};

export type DatasetTurnDisplayUnit =
  | { type: 'standard'; turn: Record<string, unknown>; index: number }
  | {
      type: 'assistant';
      turn: Record<string, unknown>;
      index: number;
      thinkingTurns: DatasetTurnRef[];
    }
  | { type: 'thinking_only'; turns: DatasetTurnRef[] };

export function groupDatasetTurnsForDisplay(turns: unknown[]): DatasetTurnDisplayUnit[] {
  const items: DatasetTurnDisplayUnit[] = [];
  let pending: DatasetTurnRef[] = [];

  const flushThinkingOnly = () => {
    if (pending.length === 0) return;
    items.push({ type: 'thinking_only', turns: pending });
    pending = [];
  };

  turns.forEach((raw, index) => {
    const turn = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};

    if (isDatasetReasoningTurn(turn)) {
      pending.push({ turn, index });
      return;
    }

    if (isDatasetAssistantTurn(turn) && pending.length > 0) {
      items.push({ type: 'assistant', turn, index, thinkingTurns: pending });
      pending = [];
      return;
    }

    flushThinkingOnly();
    items.push({ type: 'standard', turn, index });
  });

  flushThinkingOnly();
  return items;
}

export function getDatasetTurnSteps(turn: Record<string, unknown>): unknown[] {
  const steps = turn.steps ?? turn.pipeline_steps ?? turn.pipelineSteps;
  return Array.isArray(steps) ? steps : [];
}

export function formatDatasetThinkingTurnLabel(turn: Record<string, unknown>, index: number): string {
  const nameRaw = turn.agent_name ?? turn.agentName ?? turn.name;
  if (typeof nameRaw === 'string' && nameRaw.trim()) return nameRaw.trim();
  return `Thinking ${index + 1}`;
}

export function formatDatasetTurnBodyText(turn: Record<string, unknown>): string {
  const raw = turn.utterances ?? turn.text ?? turn.message ?? turn.content;
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw)) {
    return raw
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          const o = item as Record<string, unknown>;
          const body = o.content ?? o.text ?? o.message;
          if (typeof body === 'string') return body;
          try {
            return JSON.stringify(item);
          } catch {
            return String(item);
          }
        }
        return String(item);
      })
      .filter((s) => s.trim().length > 0)
      .join('\n\n');
  }
  if (raw === null || raw === undefined) return '—';
  if (typeof raw === 'object') {
    try {
      return JSON.stringify(raw);
    } catch {
      return '—';
    }
  }
  return String(raw);
}

export function formatDatasetPipelineStepLabel(step: unknown, index: number): string {
  return getPipelineStepDisplayName(step, index);
}

export function formatDatasetPipelineStepBody(step: unknown): string {
  return getDialogStepBodyDisplay(step);
}
